import moment from 'moment';
import { SETTINGS_SEARCH_INDEX } from '@ubeya/figaro/src/settings/searchIndex';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import { formatMinutesToString } from './time';

const CONFIG_PREFIX = 'config.';

const toSettingPath = (field) => (field?.startsWith(CONFIG_PREFIX) ? field.slice(CONFIG_PREFIX.length) : field);

const SETTING_LABEL_KEYS_BY_PATH = SETTINGS_SEARCH_INDEX.reduce((acc, { field, labelKey, searchLabelKey }) => {
  const settingPath = toSettingPath(field);

  if (settingPath && !acc[settingPath]) {
    acc[settingPath] = searchLabelKey || labelKey;
  }

  return acc;
}, {});

const SETTING_LABEL_KEYS = SETTINGS_SEARCH_INDEX.reduce((acc, { field, labelKey, searchLabelKey }) => {
  const settingName = field?.split('.').pop();

  if (settingName && !acc[settingName]) {
    acc[settingName] = searchLabelKey || labelKey;
  }

  return acc;
}, {});

const SCOPED_SETTING_LABEL_KEYS = {
  branch: {
    name: 'branchName'
  }
};

const SETTING_LABEL_OVERRIDES = {
  exportCode: 'branchExportCode',
  locationId: 'address',
  branchGroupId: 'settingsAuditBranchGroup',
  onsiteContact: 'onsiteContact',
  onsiteContactPhone: 'onsiteContactPhone',
  onsiteContactEmail: 'onsiteContactEmail',
  workDays: 'workDays',
  breakTypes: 'breakTypes',
  wage: 'wage',
  holidays: 'settingsAuditHolidays',
  autoBreaks: 'settingsAuditAutoBreaks',
  mobileClockMethods: 'settingsAuditMobileClockMethods',
  paymentStartDate: 'settingsAuditPaymentStartDate',
  startDay: 'settingsAuditAvailabilityWindowStartDay',
  endDay: 'settingsAuditAvailabilityWindowEndDay',
  holidayAccrualPercentage: 'accountHolidayAccrualPercentage',
  supplierWage: 'suppliersSettingsRatesWage',
  supplierClientWage: 'suppliersSettingsRatesClientWage',
  vat: 'vatPercentage',
  isAbleEditTimesheet: 'canEditTimesheets',
  toAllowRequest: 'allowDailyRequest',
  breakLength: 'breakLengthAndFrequency',
  defaultJobStartTime: 'settingsAuditDefaultJobStartTime',
  defaultJobEndTime: 'settingsAuditDefaultJobEndTime',
  fromDayNotificationHour: 'settingsAuditFromDayNotificationHour',
  untillDayNotificationHour: 'settingsAuditUntilDayNotificationHour',
  notifyEmployeeLateAfter: 'settingsAuditNotifyEmployeeLateAfter',
  notifyWorkedMoreHoursAfter: 'settingsAuditNotifyWorkedMoreHoursAfter',
  allowDailyEmployeesToEditTimesheetViaApp: 'settingsAuditAllowDailyEmployeesEditTimesheetViaApp',
  allowShiftEmployeesToEditTimesheetViaApp: 'settingsAuditAllowShiftEmployeesEditTimesheetViaApp',
  showPassCard: 'showPassCardOnApp',
  enableAvailability: 'enableAvailability',
  availabilityPublishAuto: 'enableAvailabilityAutoPush',
  availabilityPublishDay: 'dayOfWeekAvailabilitySentAutomatically',
  availabilityUnPublishDay: 'dayOfWeekAvailabilityClosedAutomatically',
  periodMarkAllDay: 'availabilityPeriodMarkAllDay',
  defaultPauseAvailabilityNotification: 'settingsDefineADefaulteNotificationPauseAvailability',
  autoShiftRemindersInMinutes: 'automaticReminder',
  autoConfirmationsInMinutes: 'automaticConfirmation',
  productivityRate: 'productivityRate',
  isDaily: 'settingsAuditNewEmployeesDaily',
  isShift: 'settingsAuditNewEmployeesShift',
  isInvoiceAutoIncrement: 'settingsAuditInvoiceAutoIncrement',
  inactivityInterval: 'settingsAuditAutoLogoutAfter'
};

const PARENT_SETTING_LABEL_KEYS = {
  supplierWage: 'suppliersSettingsRatesWage',
  supplierClientWage: 'suppliersSettingsRatesClientWage'
};

const HOLIDAY_FIELD_LABEL_KEYS = {
  end: 'endDate',
  holiday: 'holidayRegular',
  holidayOverTime1: 'holidayOvertime',
  holidayOverTime2: 'holidayOvertime2',
  isExcludedFromPayroll: 'excludedFromPayroll'
};

const TRANSLATED_NAME_SETTING_GROUPS = new Set(['modules']);

const VALUE_LABEL_KEYS = {
  defaultScheduleView: { weekly: 'schedulingBoard', monthly: 'calendar' },
  seniorityModel: {
    pools: 'seniorityModelPools',
    tenure: 'seniorityModelTenure',
    ranking: 'seniorityModelRanking',
    ranking_day_event: 'seniorityModelRankingDayEvent'
  },
  payrollVisibilityMode: {
    full: 'payrollVisibilityModeFull',
    shared_fields_only: 'payrollVisibilityModeSharedFieldsOnly'
  },
  distanceUnit: { km: 'distanceUnit_km', mi: 'distanceUnit_mi' },
  method: { 0: 'clockMethodSelf', 1: 'clockMethodEmployeeQRScan', 2: 'clockMethodManagerQRScan' }
};

const TRANSLATED_VALUE_SETTINGS = new Set([
  'defaultWeeklyGroupByView',
  'defaultMonthlyView',
  'defaultEmployeeWorkingHoursPeriod',
  'inactivityIntervalPeriod',
  'paymentPeriodType',
  'industryId'
]);

const DAY_OF_WEEK_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const DAY_OF_WEEK_SETTINGS = new Set([
  'firstDayOfWeek',
  'fromDayNotificationDay',
  'untillDayNotificationDay',
  'workDays'
]);

const DAY_OF_MONTH_SETTINGS = new Set(['startDay', 'endDay']);

const DATE_SETTINGS = new Set(['paymentStartDate', 'end']);

const INVERTED_SETTINGS = new Set(['showAreaName', 'showAreaNameUntilClockIn']);

const OFF_WHEN_EMPTY_SETTINGS = new Set([
  'autoClockOutAfter',
  'autoClockOutAfterCheckOut',
  'birthdayMessage',
  'inactivityInterval',
  'seniorityModel'
]);

const HOURS_SETTINGS = new Set(['autoClockOutAfter', 'autoClockOutAfterCheckOut']);

const ZERO_IS_OFF_SETTINGS = new Set(['inactivityInterval', 'seniorityModel']);

const TIME_VALUE_PATTERN = /^\d{2}:\d{2}(:\d{2})?$/;

const MINUTES_LIST_SETTINGS = new Set(['autoShiftRemindersInMinutes', 'autoConfirmationsInMinutes']);

const FOLLOW_ACCOUNT_WHEN_EMPTY = { payrollConfig: new Set(['dailyOvertimeIsActive']) };

const humanize = (settingName = '') =>
  settingName
    .replace(/Id$/, '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (char) => char.toUpperCase())
    .trim();

const getLeafName = (settingName) => settingName?.split('.').pop();

const translateLabel = (t, labelKey, fallbackName) => {
  if (!labelKey) {
    return humanize(fallbackName);
  }

  const label = t(labelKey);

  return !label || label.includes('<') || label.includes('{{') ? humanize(fallbackName) : label;
};

export const getSettingLabel = (t, settingName, scope, settingGroup) => {
  if (!settingName) {
    return '';
  }

  if (TRANSLATED_NAME_SETTING_GROUPS.has(settingGroup)) {
    return t(settingName);
  }

  const leafName = getLeafName(settingName);

  if (settingName.includes('.')) {
    const parentName = settingName.split('.')[0];
    const label = translateLabel(
      t,
      SETTING_LABEL_KEYS_BY_PATH[settingName] || SETTING_LABEL_OVERRIDES[leafName] || SETTING_LABEL_KEYS[leafName],
      leafName
    );
    const parentLabelKey = PARENT_SETTING_LABEL_KEYS[parentName];

    return parentLabelKey ? `${t(parentLabelKey)} – ${label}` : label;
  }

  return translateLabel(
    t,
    SCOPED_SETTING_LABEL_KEYS[scope]?.[settingName] ||
      SETTING_LABEL_OVERRIDES[settingName] ||
      SETTING_LABEL_KEYS_BY_PATH[settingName] ||
      SETTING_LABEL_KEYS[settingName],
    settingName
  );
};

export const getHolidayFieldLabel = (t, itemField) =>
  itemField ? translateLabel(t, HOLIDAY_FIELD_LABEL_KEYS[itemField], itemField) : '';

const formatDayOfWeek = (t, day) => (DAY_OF_WEEK_KEYS[Number(day) - 1] ? t(DAY_OF_WEEK_KEYS[Number(day) - 1]) : day);

const formatMinutesItem = (t, item) => {
  const minutes = typeof item === 'number' ? item : item?.minutes;
  const formatted = formatMinutesToString(Number(minutes), t);

  return item?.message ? `${formatted} ("${item.message}")` : formatted;
};

const formatListItem = (t, settingName, item) => {
  if (DAY_OF_WEEK_SETTINGS.has(settingName)) {
    return formatDayOfWeek(t, item);
  }

  if (MINUTES_LIST_SETTINGS.has(settingName)) {
    return formatMinutesItem(t, item);
  }

  if (settingName === 'autoBreaks') {
    return t('settingsAuditAutoBreakItem', {
      length: formatMinutesToString(Number(item?.length), t),
      after: formatMinutesToString(Number(item?.after), t)
    });
  }

  if (item && typeof item === 'object') {
    return item.title ?? item.name ?? JSON.stringify(item);
  }

  return item;
};

const toListItems = (value) => (Array.isArray(value) ? value : []);

const isListChange = (prevValue, newValue) =>
  (Array.isArray(prevValue) || Array.isArray(newValue)) &&
  [prevValue, newValue].every((value) => Array.isArray(value) || value === null || value === undefined);

const getMissingItems = (items, otherItems) => {
  const otherKeys = new Set(otherItems.map((item) => JSON.stringify(item)));

  return items.filter((item) => !otherKeys.has(JSON.stringify(item)));
};

export const getSettingListChange = (t, prevValue, newValue, { settingName }) => {
  if (!isListChange(prevValue, newValue)) {
    return null;
  }

  const leafName = getLeafName(settingName);
  const prevItems = toListItems(prevValue);
  const newItems = toListItems(newValue);
  const formatItems = (items) => items.map((item) => formatListItem(t, leafName, item)).join(', ');
  const added = getMissingItems(newItems, prevItems);
  const removed = getMissingItems(prevItems, newItems);

  if (!added.length && !removed.length) {
    return null;
  }

  return { addedValue: formatItems(added), removedValue: formatItems(removed) };
};

export const formatSettingValue = (t, value, { settingName, settingGroup, dateFormat, timeFormat }) => {
  const leafName = getLeafName(settingName);
  const isEmpty = value === null || value === undefined || value === '';

  if (isEmpty && FOLLOW_ACCOUNT_WHEN_EMPTY[settingGroup]?.has(leafName)) {
    return t('settingsAuditFollowAccountSettings');
  }

  if ((isEmpty || (value === 0 && ZERO_IS_OFF_SETTINGS.has(leafName))) && OFF_WHEN_EMPTY_SETTINGS.has(leafName)) {
    return t('off');
  }

  if (isEmpty) {
    return t('empty');
  }

  if (typeof value === 'boolean') {
    const isOn = INVERTED_SETTINGS.has(leafName) ? !value : value;

    return isOn ? t('on') : t('off');
  }

  if (Array.isArray(value)) {
    return value.length ? value.map((item) => formatListItem(t, leafName, item)).join(', ') : t('none');
  }

  const valueLabelKey = VALUE_LABEL_KEYS[leafName]?.[value];

  if (valueLabelKey) {
    return t(valueLabelKey);
  }

  if (TRANSLATED_VALUE_SETTINGS.has(leafName)) {
    return t(value);
  }

  if (DAY_OF_WEEK_SETTINGS.has(leafName)) {
    return formatDayOfWeek(t, value);
  }

  if (DAY_OF_MONTH_SETTINGS.has(leafName)) {
    return `${t('day')} ${value}`;
  }

  if (DATE_SETTINGS.has(leafName)) {
    return moment(value, API_DATE_FORMAT).format(dateFormat);
  }

  if (timeFormat && typeof value === 'string' && TIME_VALUE_PATTERN.test(value)) {
    return moment(value, 'HH:mm:ss').format(timeFormat);
  }

  if (HOURS_SETTINGS.has(leafName)) {
    return `${value} ${t('hours')}`;
  }

  if (leafName === 'hourFormat') {
    return `${value} ${t('hours')}`;
  }

  if (leafName === 'vat') {
    return `${Math.round((Number(value) - 1) * 10000) / 100}%`;
  }

  return value;
};
